import constants from '@playcastdotio/GlobalConfig';
import type { SlugUrlConfig } from '@playcastdotio/Messaging';
/**
 * Campaign URL Mapping
 *
 * Maps obfuscated URL slugs to readable campaign IDs.
 *
 * - Public URLs: /q/{obfuscatedSlug} (not guessable, safe to share)
 * - Internal: campaignId (readable, used in analytics, logs, admin)
 *
 * IMPORTANT: Always use campaignId in analytics events, NOT the URL slug.
 * This ensures dashboards remain readable regardless of URL obfuscation.
 *
 * @see docs/MATCHMAKING_ARCHITECTURE_DECEMBER_2025.md Section 4.2
 */

// ============================================================================
// URL MAPPINGS (December 2025)
// ============================================================================

/**
 * Obfuscated URL slug → Readable campaign ID
 *
 * Used when: Player visits /q/{slug}, we need to look up the campaign
 *
 * Example: URL /q/af5ea5f1 → campaignId 'shades'
 */
export const URL_TO_CAMPAIGN: Record<string, string> = {
  // Test/development slugs (keep for testing)
  'test': 'test',               // Test campaign (not obfuscated)
  'quick': 'quick',             // Quick test campaign (not obfuscated)
  'any': 'any',                 // Any campaign (not obfuscated)
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get campaign ID from URL slug (synchronous fallback version)
 * Use getCampaignIdFromUrlAsync() for API-backed resolution
 *
 * @param urlSlug - The slug from the URL (e.g., 'x7k9m2')
 * @returns The campaign ID (e.g., 'shades') or null if not found
 *
 * @example
 * getCampaignIdFromUrl('x7k9m2') // 'shades'
 * getCampaignIdFromUrl('unknown') // null
 */
export function getCampaignIdFromUrl(urlSlug: string): string | null {
  // Handle tilde-separated tracking data (e.g., 'x7k9m2~utm_source~campaign')
  const baseSlug = urlSlug.split('~')[0];
  return URL_TO_CAMPAIGN[baseSlug] ?? null;
}

/**
 * Get campaign ID from URL slug (async version with API fetch)
 *
 * Tries to resolve from API first (Redis storage), falls back to hardcoded mapping if:
 * - API call fails
 * - Slug not found in Redis
 * - Network error
 *
 * @param urlSlug - The slug from the URL (e.g., 'x7k9m2')
 * @returns Promise resolving to campaign ID or null if not found
 *
 * @example
 * const campaignId = await getCampaignIdFromUrlAsync('x7k9m2'); // 'shades'
 */
export async function getCampaignIdFromUrlAsync(urlSlug: string): Promise<string | null> {
  // Handle tilde-separated tracking data (e.g., 'x7k9m2~utm_source~campaign')
  const baseSlug = urlSlug.split('~')[0];

  try {
    // Try to fetch from API first (Redis-backed with admin-configured mappings)
    const apiUrl = `${constants.REALTIME_API_BASE_URL}realtime/campaigns/resolve/${encodeURIComponent(baseSlug)}`;

    const response = await fetch(apiUrl);

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.campaignId) {
        console.log(`[Campaign URL] Resolved "${baseSlug}" → "${data.campaignId}" from API`);
        return data.campaignId;
      }
    }

    // API didn't have it, fall back to hardcoded
    console.log(`[Campaign URL] API resolution failed for "${baseSlug}", falling back to hardcoded mapping`);
  } catch (error) {
    console.warn(`[Campaign URL] Error resolving slug from API for "${baseSlug}":`, error);
  }

  // Fallback to hardcoded mapping
  const campaignId = URL_TO_CAMPAIGN[baseSlug] ?? null;
  if (campaignId) {
    console.log(`[Campaign URL] Using hardcoded mapping: "${baseSlug}" → "${campaignId}"`);
  } else {
    console.warn(`[Campaign URL] No mapping found for slug: "${baseSlug}"`);
  }

  return campaignId;
}

/**
 * Check if a URL slug is valid (exists in mapping)
 *
 * @param urlSlug - The slug from the URL
 * @returns true if valid, false otherwise
 */
export function isValidCampaignUrl(urlSlug: string): boolean {
  const baseSlug = urlSlug.split('~')[0];
  return baseSlug in URL_TO_CAMPAIGN;
}

/**
 * Get all available campaign IDs
 *
 * @returns Array of all campaign IDs
 */
export function getAllCampaignIds(): string[] {
  return Object.values(URL_TO_CAMPAIGN);
}

/**
 * Get all available URL slugs
 *
 * @returns Array of all URL slugs
 */
export function getAllUrlSlugs(): string[] {
  return Object.keys(URL_TO_CAMPAIGN);
}

/**
 * Normalize URL slug to campaign ID
 *
 * This is useful when you need to compare URL slugs with theme campaign IDs.
 * Handles both obfuscated URLs (e.g., '7e0f6630') and direct campaign IDs (e.g., 'fortnite-odyssey').
 * Falls back to the input value if no mapping exists (for backwards compatibility with test/dev slugs).
 *
 * @param urlSlug - The slug from the URL (obfuscated or direct)
 * @returns The canonical campaign ID
 *
 * @example
 * normalizeUrlSlugToCampaignId('7e0f6630') // 'fortnite-odyssey'
 * normalizeUrlSlugToCampaignId('fortnite-odyssey') // 'fortnite-odyssey'
 * normalizeUrlSlugToCampaignId('test') // 'test'
 */
export function normalizeUrlSlugToCampaignId(urlSlug: string): string {
  return getCampaignIdFromUrl(urlSlug) || urlSlug;
}

/**
 * Fetch per-slug URL configuration from API.
 * Returns null if no config exists (client falls back to player theme).
 *
 * @param urlSlug - The raw slug (may include tilde-encoded suffixes, which are stripped)
 */
export async function getSlugUrlConfigAsync(urlSlug: string): Promise<SlugUrlConfig | null> {
  const baseSlug = urlSlug.split('~')[0];
  try {
    const apiUrl = `${constants.REALTIME_API_BASE_URL}realtime/campaigns/resolve/${encodeURIComponent(baseSlug)}/urls`;
    const response = await fetch(apiUrl);

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data) {
        console.log(`[Campaign URL] Loaded slug URL config for "${baseSlug}"`);
        return data.data as SlugUrlConfig;
      }
    }

    return null;
  } catch (error) {
    console.warn(`[Campaign URL] Error fetching slug URL config for "${baseSlug}":`, error);
    return null;
  }
}
